/**
 * The catalogue in memory, for the instant answer.
 *
 * "char 006" should be on screen before the finger lifts. The database search
 * is smart but it is a round trip with a 250ms debounce in front of it, and on
 * a card show's wifi that is a second or a blank. So every search box asks this
 * first: a snapshot of every card's id, set, number and name, loaded once per
 * session, matched with the same rules the database uses.
 *
 * It is a preview, never the verdict. It knows no prices and no pictures, so
 * its rows carry neither, and the grid draws them as names with a placeholder.
 * The database pass that was going to run anyway still runs, and its rows,
 * priced and pictured, replace these the moment they land. If the snapshot is
 * stale a new card is simply not instant; it is still found.
 *
 * Kept as a separate lazy chunk so the first paint of the app never pays for it.
 */
import { matchSets, residualWords, rankCards, isSearchable } from '../utils/smartSearch';

let index = null;
let sets = null;
let setNames = null;
let loading = null;

/** Start loading the snapshot. Safe to call often; only the first call does anything. */
export function warmCatalogIndex() {
  if (index || loading) return loading || Promise.resolve(index);
  loading = import('../data/catalog-index.json')
    .then((mod) => {
      const data = mod.default || mod;
      sets = (data.sets || []).map(([id, name, abbreviation, total, year, promo, digital]) => ({
        id, name, abbreviation, card_count_official: total, year,
        promo: promo === 1,
        digital: digital === 1,
      }));
      setNames = new Map(sets.map((s) => [s.id, s.name]));
      index = (data.cards || []).map(([id, setId, localId, name, hp]) => {
        const lname = String(name || '').toLowerCase();
        return {
          id,
          set_id: setId,
          local_id: localId,
          name,
          hp: hp || null,
          lname,
          uid: String(localId || '').toUpperCase(),
          // The number with its zero padding gone, so "004" and "4" are one
          // key, and the name as plain words, both precomputed once here
          // because X-Ray compares every card against a read on every scan.
          nid: bareId(localId),
          words: lname.replace(/[^a-z ]/g, '').trim().split(/\s+/).filter(Boolean),
        };
      });
      return index;
    })
    .catch((e) => {
      // No snapshot means no instant answer, and nothing else. The database
      // search still runs.
      console.warn('[catalogIndex] not loaded:', e?.message || e);
      loading = null;
      return null;
    });
  return loading;
}

/** "004" -> "4", "TG05" -> "TG5", "SWSH151" -> "SWSH151". One key per printed number. */
export function bareId(localId) {
  return String(localId || '').toUpperCase().replace(/^([A-Z]*)0+(?=\d)/, '$1');
}

/**
 * Every card in the snapshot, for a caller that wants to walk them itself.
 *
 * X-Ray does. Its read is a name the camera may have misspelt and a number it
 * may have padded, and the search tiers above are built for a person typing,
 * who spells the name right and expects forty rows. See identifyOffline.
 */
export function catalogRows() {
  return index || [];
}

let known = null;

/**
 * Every collector number the catalogue prints, padding removed, and every
 * letter prefix it uses ("SWSH", "TG", "GG"). Built once, on first use. X-Ray
 * checks a read against these: a number nobody printed is a misread, and a
 * prefix nobody uses ("X2", off a weakness box) is not a number at all.
 */
export function knownNumbers() {
  if (!known && index) {
    known = { ids: new Set(), prefixes: new Set() };
    for (const c of index) {
      known.ids.add(c.nid);
      const m = c.nid.match(/^([A-Z]+)\d/);
      if (m) known.prefixes.add(m[1]);
    }
  }
  return known || { ids: new Set(), prefixes: new Set() };
}

/** A snapshot card shaped the way every catalogue grid expects a row. */
export function rowFor(c) {
  return {
    id: c.id,
    tcgdex_card_id: c.id,
    set_id: c.set_id,
    local_id: c.local_id,
    name: c.name,
    set_name: setNames?.get(c.set_id) || c.set_id,
    image_url: null,
    near_mint: null,
    _instant: true,
  };
}

/** Whether an instant answer is possible right now, without waiting. */
export function catalogIndexReady() {
  return Boolean(index);
}

/**
 * Every set, from the snapshot.
 *
 * The app's own set list comes from the database, so with no signal a search
 * result used to name a card and leave the set blank beside it. This is the
 * same list, already in memory, and it is what a caller passes back in as
 * `sets` when there is nothing else to pass.
 */
export function localSets() {
  return sets || [];
}

/**
 * The sets that have exactly this many cards in them.
 *
 * "4/102" says Base Set louder than "Charizard" does: the 102 rules out 295 of
 * the 297 sets on its own. X-Ray reads that denominator off the card and this
 * turns it into the short list.
 */
export function setsWithCount(total) {
  const n = parseInt(total, 10);
  if (!n || !sets) return [];
  return sets.filter((s) => s.card_count_official === n);
}

/**
 * The instant answer for a parsed query, or null when the snapshot is not
 * loaded yet.
 *
 * Tiers, same as smartCatalogSearch so the two never disagree about what a
 * query means:
 *   1. name words AND card number
 *   2. a set explains some words; look inside it for the rest
 *   3. a set-code prefix stuck to the number: trust the prefixed number
 *   4. the number alone, then the name alone
 *
 * @param {object} parsed from utils/smartSearch.parseQuery
 * @param {{ sets?: object[], limit?: number }} [options]
 *   `sets` is the catalogue set list already in memory, for tier 2 and for the
 *   set name on each row.
 * @returns {object[]|null} rows shaped for CatalogCardGrid, best first
 */
export function searchCatalogIndex(parsed, { sets: setList, limit = 60 } = {}) {
  if (!index) return null;
  if (!isSearchable(parsed)) return [];

  // Falls back to the snapshot's own set list, so a caller with no network and
  // nothing loaded still gets set names on its rows.
  const known = setList && setList.length ? setList : (sets || []);
  const words = parsed.words || [];
  const localIds = new Set(parsed.localIds || []);
  const setName = new Map(known.map((s) => [s.id, s.name]));

  const run = (terms, ids, setIds) => {
    const out = [];
    for (const card of index) {
      if (setIds && !setIds.has(card.set_id)) continue;
      if (ids && !ids.has(card.uid)) continue;
      if (terms.length && !terms.every((w) => card.lname.includes(w))) continue;
      out.push(card);
      // Plenty for ranking; the prefix pass below decides what is first.
      if (out.length >= limit * 4) break;
    }
    return out;
  };

  const tiers = [[words, localIds.size ? localIds : null, null]];
  if (words.length) {
    const setHits = matchSets(parsed, known);
    const residual = residualWords(parsed, setHits);
    if (setHits.length && residual.length < words.length) {
      tiers.push([residual, localIds.size ? localIds : null, new Set(setHits.map((s) => s.id))]);
    }
  }
  if (localIds.size && words.length) {
    const prefixed = new Set([...localIds].filter((id) => /^[A-Z]+\d/.test(id)));
    if (prefixed.size) tiers.push([[], prefixed, null]);
    tiers.push([[], localIds, null]);
    tiers.push([words, null, null]);
  }

  for (const [terms, ids, setIds] of tiers) {
    if (!terms.length && !ids) continue;
    const hits = run(terms, ids, setIds);
    if (hits.length) {
      return rankCards(parsed, hits).slice(0, limit).map((c) => ({
        id: c.id,
        tcgdex_card_id: c.id,
        set_id: c.set_id,
        local_id: c.local_id,
        name: c.name,
        set_name: setName.get(c.set_id) || c.set_id,
        image_url: null,
        near_mint: null,
        _instant: true,
      }));
    }
  }
  return [];
}
